// Shared state for the chunk-reload guard. Two consumers use this:
//
//   1. `useChunkErrorListener` in App.tsx — catches unhandled promise
//      rejections from lazy() chunk loads that escape React Suspense.
//   2. `ErrorBoundary.getDerivedStateFromError` — catches the same
//      errors when they DO reach the React render boundary.
//
// Before this module both code paths owned their own logic AND wrote to
// the same `chunk-reload-attempted` sessionStorage key with INCOMPATIBLE
// formats (App.tsx wrote the literal string `"true"`; ErrorBoundary
// wrote `JSON.stringify({ts, count})`). Each handler clobbered the
// other's state, the TTL+counter cap from #1313 never triggered correctly,
// and users saw the "A new version is available" UI in a loop with no
// reliable way out. See follow-up to #1313.
//
// Single source of truth now:
//   - One key, one shape, one set of helpers.
//   - 30 s sliding TTL: counter resets if the user pauses between fails.
//   - 3-attempt cap: after that, the boundary surfaces the persistent UI
//     so the user has an explicit recovery path instead of an infinite
//     reload storm.

export const CHUNK_RELOAD_KEY = 'chunk-reload-attempted';
export const CHUNK_RELOAD_TTL_MS = 30_000;
export const MAX_RELOAD_ATTEMPTS = 3;

export interface ReloadState {
  ts: number;
  count: number;
}

/** Detects stale-chunk / dynamic-import errors caused by redeployments. */
export function isChunkLoadError(messageOrError: string | Error | null | undefined): boolean {
  const msg = typeof messageOrError === 'string'
    ? messageOrError
    : (messageOrError?.message ?? '');
  if (!msg) return false;
  return /dynamically imported module|Loading chunk|load module script/i.test(msg);
}

/**
 * Read the sessionStorage flag. Returns null when missing, malformed,
 * or past TTL so callers treat it as "no recent attempt."
 */
export function readReloadState(): ReloadState | null {
  try {
    const raw = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      typeof (parsed as { ts?: unknown }).ts !== 'number' ||
      typeof (parsed as { count?: unknown }).count !== 'number'
    ) {
      // Legacy data from before this module landed (the App.tsx handler
      // used to write the literal string "true"). Treat as cleared so the
      // user gets a fresh window instead of getting stuck.
      return null;
    }
    const state = parsed as ReloadState;
    if (Date.now() - state.ts > CHUNK_RELOAD_TTL_MS) return null;
    return state;
  } catch {
    return null;
  }
}

export function writeReloadState(count: number): void {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, JSON.stringify({ ts: Date.now(), count }));
  } catch {
    // sessionStorage can throw on quota / disabled cookies — best-effort.
  }
}

export function clearReloadState(): void {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch {
    // ditto
  }
}

/**
 * Shared "should I auto-reload?" decision. Returns `true` and bumps the
 * counter when the caller should reload; returns `false` when the cap is
 * already hit and the caller should surface a persistent UI instead.
 *
 * Both consumers (the global unhandledrejection listener and the React
 * ErrorBoundary) call this so they cooperate cleanly on a single counter.
 */
export function shouldAutoReload(): boolean {
  const state = readReloadState();
  const attemptCount = state ? state.count : 0;
  if (attemptCount >= MAX_RELOAD_ATTEMPTS) return false;
  writeReloadState(attemptCount + 1);
  return true;
}
